import { Check as CheckIcon, Close as CloseIcon } from '@mui/icons-material';
import { Grid } from '@mui/material';
import { HSpacer, RoutalPalette } from '@nx-smartmonkey/ui';
import { useParams } from 'react-router-dom';

import iconNorthStar from '../../../../../../assets/img/shared/icon-northStar.svg';
import UndoStopIcon from '../../../../../../assets/svg/UndoStopIcon';
import { LoadingButton } from '../../../../../../components/LoadingButton';
import { STOP_STATUS } from '../../../../../../constants/Stops';
import { Stop } from '../../../../../../domain/stop/Stop';
import { Text } from '../../../../../../react-components/Text';
import { useRetrieveVehicle } from '../../../../hooks/useRetrieveVehicle';
import { useRouteContext } from '../../../context/context';

interface StopDetailsFooterProps {
  onCancel: (stopId: string) => void;
  onComplete: (stopId: string) => void;
  onDiscard: (stopId: string) => void;
  onNextStop: (stopId: string) => void;
  onStartStop: () => void;
  canUpdateRoute: boolean;
  isCounting?: boolean;
  stop?: Stop;
  discardLoader?: boolean;
  reportLoader?: boolean;
}

export const StopDetailsFooter = ({
  onCancel,
  onComplete,
  onDiscard,
  onNextStop,
  onStartStop,
  canUpdateRoute,
  isCounting,
  stop,
  discardLoader,
  reportLoader,
}: StopDetailsFooterProps) => {
  const { stopId } = useParams();
  const { data: vehicleData } = useRetrieveVehicle();
  const { routeState } = useRouteContext();

  if (!stop) {
    return null;
  }

  const reportButtons = () => {
    return (
      <>
        <LoadingButton
          isLoading={reportLoader}
          style={{
            margin: `3px`,
            height: `40px`,
            borderRadius: `10px`,
            width: `100%`,
            backgroundColor: RoutalPalette.white,
            border: `1px solid ${RoutalPalette.error.medium}`,
            display: `flex`,
            flexDirection: `row`,
            flexWrap: `nowrap`,
            justifyContent: `center`,
            alignItems: `center`,
          }}
          onClick={(e: any) => {
            if (stopId) onCancel(stopId);
          }}
        >
          <Grid container direction="row" justifyContent="center" alignItems="center">
            <CloseIcon style={{ fill: RoutalPalette.error.medium, height: `22px` }} />
            <Text intlId={`driver.stop.entry.cancel`} style={{ color: RoutalPalette.error.medium, fontSize: `14px` }} />
          </Grid>
        </LoadingButton>
        <LoadingButton
          isLoading={reportLoader}
          style={{
            margin: `3px`,
            height: `40px`,
            borderRadius: `10px`,
            width: `100%`,
            fontWeight: `normal`,
            backgroundColor: RoutalPalette.success.medium,
            borderColor: RoutalPalette.success.medium,
            color: RoutalPalette.white,
            border: `1px solid ${RoutalPalette.success.medium}`,
            display: `flex`,
            flexDirection: `row`,
            flexWrap: `nowrap`,
            justifyContent: `center`,
            alignItems: `center`,
          }}
          onClick={(e: any) => {
            if (stopId) onComplete(stopId);
          }}
        >
          <Grid container direction="row" justifyContent="center" alignItems="center">
            <CheckIcon style={{ fill: RoutalPalette.white, height: `22px` }} />
            <Text intlId={`driver.stop.entry.complete`} style={{ color: RoutalPalette.white, fontSize: `14px` }} />
          </Grid>
        </LoadingButton>
      </>
    );
  };

  const discardButtons = () => {
    return (
      <LoadingButton
        isLoading={discardLoader}
        variant="text"
        style={{
          height: `42px`,
          width: `100%`,
          display: `flex`,
          flexDirection: `row`,
          flexWrap: `nowrap`,
          justifyContent: `center`,
          alignItems: `center`,
        }}
        onClick={(e: any) => {
          if (stopId) onDiscard(stopId);
        }}
      >
        <UndoStopIcon style={{ fill: RoutalPalette.primary40, width: `16px` }} />
        <HSpacer small />

        <Text
          intlId={`driver.stop.discard_report.title`}
          style={{ color: RoutalPalette.primary40, fontSize: `14px`, fontWeight: `normal` }}
        />
      </LoadingButton>
    );
  };

  const startStopButton = () => {
    return (
      <LoadingButton
        isLoading={discardLoader}
        // variant="text"
        style={{
          height: `42px`,
          width: `100%`,
          display: `flex`,
          flexDirection: `row`,
          flexWrap: `nowrap`,
          justifyContent: `center`,
          alignItems: `center`,
        }}
        onClick={(e: any) => {
          if (stopId) onStartStop();
        }}
      >
        <Text
          intlId={`driver.stop.start_time`}
          style={{ color: RoutalPalette.white, fontSize: `14px`, fontWeight: `normal` }}
        />
        <HSpacer small />
        <img src={iconNorthStar} alt="Customer fields" style={{ height: `15px`, width: `15px` }} />
      </LoadingButton>
    );
  };

  const nextStopButton = () => {
    return (
      <LoadingButton
        isLoading={discardLoader}
        // variant="text"
        style={{
          height: `42px`,
          width: `100%`,
          display: `flex`,
          flexDirection: `row`,
          flexWrap: `nowrap`,
          justifyContent: `center`,
          alignItems: `center`,
        }}
        onClick={(e: any) => {
          if (stopId) onNextStop(stopId);
        }}
      >
        <Text
          intlId={`driver.stop.next_stop`}
          style={{ color: RoutalPalette.white, fontSize: `14px`, fontWeight: `normal` }}
        />
        <HSpacer small />
        <img src={iconNorthStar} alt="Customer fields" style={{ height: `15px`, width: `15px` }} />
      </LoadingButton>
    );
  };

  return (
    <Grid
      container
      style={{
        display: `flex`,
        flexDirection: `row`,
        justifyContent: `space-evenly`,
        alignContent: `center`,
        flexWrap: `nowrap`,
        padding: `0px 20px`,
        height: `70px`,
        alignItems: `center`,
        borderTop: `1px solid ${RoutalPalette.neutral10}`,
      }}
    >
      {stop.status !== STOP_STATUS.pending
        ? discardButtons()
        : canUpdateRoute
        ? nextStopButton()
        : !vehicleData?.project.fields.configuration.includes(`stop_duration`)
        ? reportButtons()
        : !isCounting
        ? startStopButton()
        : routeState.startedReportStop === stop.id
        ? reportButtons()
        : null}
    </Grid>
  );
};
